import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface BrandRow {
  brand_key: string;
  name: string;
  primary_domain: string;
  customer_cognito_user_pool_id: string;
  customer_cognito_client_id: string | null;
  admin_cognito_user_pool_id: string;
  admin_cognito_client_id: string | null;
  is_active: boolean;
  frontend_url: string | null;
  primary_colour: string | null;
  secondary_colour: string | null;
  email_header_colour: string | null;
  logo_primary_url: string | null;
  logo_secondary_url: string | null;
  warning_bg_colour: string | null;
  warning_border_colour: string | null;
  warning_text_colour: string | null;
  warning_panel_colour: string | null;
  support_email: string | null;
  admin_notification_email: string | null;
  transactional_from_email: string | null;
  transactional_from_name: string | null;
  marketing_from_email: string | null;
  marketing_from_name: string | null;
  business_legal_name: string | null;
  business_abn: string | null;
  business_address_street: string | null;
  business_address_suburb: string | null;
  business_address_state: string | null;
  business_address_postcode: string | null;
  business_phone: string | null;
  brevo_marketing_list_id: number | null;
  brevo_weekly_tips_list_id: number | null;
  brevo_midweek_tips_list_id: number | null;
  brevo_free_tips_list_id: number | null;
  brevo_test_list_id: number | null;
  facebook_page_id: string | null;
  social_image_bucket: string | null;
  social_image_base_url: string | null;
  social_footer_text: string | null;
}

export interface SecretSummary {
  name: string;
  label: string;
  hint: string;
  configured: boolean;
  last4: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  envOverride: boolean;
}

export interface BrandSettings {
  brand: BrandRow;
  secretsEnabled: boolean;
  secrets: SecretSummary[];
}

export type BrandSettingsPatch = Partial<Record<keyof BrandRow, string | number | null>>;

const KEY = ['brand-settings'];

export function useBrandSettings() {
  return useQuery({ queryKey: KEY, queryFn: () => apiClient<BrandSettings>('/brand-settings') });
}

export function useUpdateBrandSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: BrandSettingsPatch) =>
      apiClient<{ brand: BrandRow }>('/brand-settings', { method: 'PUT', body: JSON.stringify(patch) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export interface SecretUpdateResult {
  name: string;
  last4: string;
  updatedAt: string;
  updatedBy: string;
  propagationMinutes: number;
}

/** Write-only: the value goes up, only its last4 ever comes back. */
export function useUpdateBrandSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) =>
      apiClient<SecretUpdateResult>(`/brand-settings/secrets/${encodeURIComponent(name)}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
